import React from 'react'
import { PayPalScriptProvider } from '@paypal/react-paypal-js'
import { FREQUENCY } from '@classy/campaign-page-blocks'
import paypalApi from 'redux/services/paypal'
import { PaymentMethod } from 'models/ChargeIntent'
import { useGetFeeEstimateQuery } from 'redux/services/fees'
import PaypalButtonWrapper from './paypal-button-wrapper'
import Placeloader from 'modules/ui/atoms/Loaders/Placeloader'
import { ActionButton } from 'modules/ui/atoms/ActionButton'
import LoadingSpinnerComponent from 'modules/ui/atoms/Loading/LoadingSpinner'
import { selectContrastingColor } from '@classy/campaign-page-blocks'
import useOrder from 'modules/order/state/order/use-order'
import useConfig from 'modules/config/state/use-config'
import useStatus from 'modules/status/state/use-status'
import { StatusType } from 'modules/status/state/status-types'
import { PaymentSelectionButtonProps } from './payment-selection-button'

export const PaypalButton = ({ paymentMethodConfig }: PaymentSelectionButtonProps) => {
  const order = useOrder()
  const fee = usePaypalDCFFee()
  const status = useStatus()

  const paypalOrVenmoSelected =
    order.paymentDetails.type === PaymentMethod.Venmo ||
    order.paymentDetails.type === PaymentMethod.PayPal_Commerce

  const fundingSource = paymentMethodConfig.key === PaymentMethod.Venmo ? 'venmo' : 'paypal'
  const paypalScriptOptions = usePaypalScriptOptions(fundingSource)

  // Show loading state when status is loading
  const isLoading = status.type === StatusType.SUBMITTING || status.type === StatusType.PROCESSING

  if (
    !paypalScriptOptions ||
    (paypalOrVenmoSelected && order.paymentDetails.type !== paymentMethodConfig.key)
  ) {
    return <Placeloader style={{ width: '100%', height: 55, borderRadius: 16 }} />
  }

  // Show loading button when status is loading
  if (isLoading) {
    const buttonColor = fundingSource === 'venmo' ? '#0087f6' : '#FFC107' // Venmo blue or PayPal yellow

    return (
      <ActionButton disabled color={buttonColor} size="lg" borderRadius="12px">
        <LoadingSpinnerComponent size="sm" color={selectContrastingColor(buttonColor)} />
      </ActionButton>
    )
  }

  return (
    // deferLoading necessary to avoid reloading paypal SDK on every re-render
    <PayPalScriptProvider options={paypalScriptOptions} deferLoading>
      <PaypalButtonWrapper fee={fee <= 0 ? 0 : fee} fundingSource={fundingSource} />
    </PayPalScriptProvider>
  )
}

const usePaypalDCFFee = () => {
  const PAYPAL_FEE_RATE = 0.0185
  const PAYPAL_FEE_FIXED = 0.3

  const order = useOrder()
  const config = useConfig()

  const { processor, type, isAmex } = order.paymentDetails

  // based on appv2 paypal DCF logic, https://bitbucket.org/stayclassy/appv2/src/2f45372e4ac3d0f7320ef9b46b94e7172eac1166/global/classy-pay/components/cpPaypalCommerceWidget/cpPaypalCommerceWidget.component.js#lines-80
  const { data: feeData } = useGetFeeEstimateQuery(
    {
      campaignId: config.campaignId,
      params: {
        amount: order.amount.value,
        fot: order.dcf.value.isChecked,
        currency: order.currency.value,
        processor_name: processor,
        payment_method: type,
        amex: isAmex,
      },
    },
    { skip: !order.amount.value },
  )

  // TODO Gross Adjustment properties are actually strings but are currently modeled as numbers
  const feeSum = feeData
    ? // @ts-expect-error | should be number
      parseFloat(feeData.gross_adjustment.raw_processor_fee_amount) +
      // @ts-expect-error | should be number
      parseFloat(feeData.gross_adjustment.raw_application_fee_amount)
    : 0

  const fee = feeSum - order.dcf.value.totalAmount * PAYPAL_FEE_RATE - PAYPAL_FEE_FIXED

  return React.useMemo(() => fee, [fee])
}

const usePaypalScriptOptions = (fundingSource: 'paypal' | 'venmo') => {
  const order = useOrder()
  const config = useConfig()

  const { data: tokenData } = paypalApi.useGetPaypalTokenQuery({
    campaignId: config.campaignId,
    currency: order.currency.value,
  })

  if (!tokenData) {
    return undefined
  }

  // https://developer.paypal.com/sdk/js/configuration/
  const SCRIPT_PROVIDER_OPTIONS = {
    clientId: tokenData?.clientId,
    merchantId: tokenData?.merchantId,
    currency: order.currency.value,
    intent: 'capture',
    enableFunding: fundingSource,
    disableFunding: 'card',
    // debug: true
  }

  const SCRIPT_PROVIDER_OPTIONS_RECURRING = {
    clientId: tokenData?.clientId,
    // merchantId: tokenData?.merchantId,
    currency: order.currency.value,
    components: 'buttons,funding-eligibility',
    // dataNamespace: 'paypalRecurring',
    intent: 'tokenize',
    enableFunding: 'paypal',
    disableFunding: 'card',
    vault: true,
    // debug: true
  }

  const options =
    order.frequency.value !== FREQUENCY.ONE_TIME
      ? SCRIPT_PROVIDER_OPTIONS_RECURRING
      : SCRIPT_PROVIDER_OPTIONS

  return options
}
